import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type AgencyVisit,
  type AgencyVisitDetail,
  type AgencyVisitFormValues,
  AgencyVisitSchema,
  AgencyVisitDetailSchema,
} from "../models/agencyVisit"
import { agencyVisitApi } from "../services/agencyVisitApi"
import { useCrudList, useCrudItem } from "../../../hooks/useCrud"

const BASE_KEY = "agency-visits"
const BASE_URL = "agency-visit"

export function useAgencyVisits(filters?: Record<string, unknown>) {
  return useCrudList<AgencyVisit>(BASE_KEY, BASE_URL, AgencyVisitSchema, filters)
}

export function useAgencyVisit(id?: number | null) {
  return useCrudItem<AgencyVisitDetail>(BASE_KEY, BASE_URL, AgencyVisitDetailSchema, id)
}

export function useCreateAgencyVisit() {
  const queryClient = useQueryClient()
  return useMutation<AgencyVisitDetail, Error, AgencyVisitFormValues>({
    mutationFn: (values) => agencyVisitApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateAgencyVisit() {
  const queryClient = useQueryClient()
  return useMutation<AgencyVisitDetail, Error, { id: number; values: AgencyVisitFormValues }>({
    mutationFn: ({ id, values }) => agencyVisitApi.update(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteAgencyVisit() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => agencyVisitApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateVisitStar() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { id: number; rating: number }>({
    mutationFn: ({ id, rating }) => agencyVisitApi.updateStar(id, rating),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, variables.id] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useVisitUsers() {
  return useQuery({
    queryKey: [BASE_KEY, "users"],
    queryFn: () => agencyVisitApi.getUsers(),
    staleTime: 1000 * 60 * 30,
  })
}

export function useVisitAgencies() {
  return useQuery({
    queryKey: [BASE_KEY, "agencies"],
    queryFn: () => agencyVisitApi.getAgencies(),
    staleTime: 1000 * 60 * 30,
  })
}
